import React, { PureComponent } from 'react';

import { observer } from 'mobx-react';
import { NavigateFunction } from 'react-router';
import withNavigateParams from '../../utils/withNavigateParams';
import { observable } from 'mobx';
import {
	admin_attachPlace,
	admin_deletePlace,
	admin_detachPlace,
	admin_fetchPlace,
	manager_attachEmployee,
	manager_introducePlaceEmployee,
	manager_invitePlaceEmployee,
	manager_updatePlaceName,
	manager_updatePlacePaymentData,
	manager_updatePlacePro,
	manager_uploadPlaceAvatar,
} from '../../lib/api-web';
import toaster from '../../stores/toaster';
import AdminPlaceData from '../../web-types/AdminPlaceData';
import { Button, Menu, Spin, Typography } from 'antd';
import {
	PlusOutlined,
	HomeOutlined,
	UploadOutlined,
	DeleteOutlined,
	EditOutlined,
	DollarCircleOutlined,
	DisconnectOutlined,
	AppstoreAddOutlined,
	UserAddOutlined,
} from '@ant-design/icons';
import { TPage, TPContent, TPSidebar, TPSidebarHeader } from '../../controls/TPage';
import Avatar from 'antd/lib/avatar/avatar';
import AvatarUploadModal from '../../global-modals/AvatarUploadModal';
import dataUriToBlob from '../../utils/dataUriToBlob';
import { StatDiv, StatHeader, StatPlate, StatPlateTitle, StatPlateValue } from '../../controls/StatHeader';
import PromptModal from '../../global-modals/PromptModal';
import handleError from '../../utils/handleError';
import { AdminPlaceMetaData } from '../../web-types/AdminPlaceMetaData';
import formatNumber from '../../utils/formatNumber';
import formatMoney from '../../utils/formatMoney';
import isValidPhone from '../../utils/isValidPhone';
import ConfirmModal from '../../global-modals/ConfirmModal';
import BackButton from '../../controls/BackButton';
import AddEmployeeModal from '../../global-modals/AddEmployeeModal';
import QRModal from '../../global-modals/QRModal';
import user from '../../stores/user';
import SimpleEmployeeModal from '../../global-modals/SimpleEmployeeModal';
import SelectChainModal from '../../global-modals/SelectChainModal';
import SelectEmployeeModal from '../../global-modals/SelectEmployeeModal';
import EmployeeRoleModal from '../../global-modals/EmployeeRoleModal';
import UserPlaceRole from '../../web-types/UserPlaceRole';

interface AdminPlaceCardProps {
	navigate: NavigateFunction;
	params: { placeId: string };
	state: { backable: boolean } | null;
}

@observer
class AdminPlaceCard extends PureComponent<AdminPlaceCardProps> {
	@observable loading = false;
	@observable avatarUploading = false;
	@observable nameEditing = false;
	@observable employeeInviting = false;
	@observable addEmployeeing = false;
	@observable paymentDataEditing = false;
	@observable placeDeleting = false;
	@observable placeDetaching = false;
	@observable data: AdminPlaceData | null = null;
	@observable metadata: AdminPlaceMetaData | null = null;
	@observable imageUrl: string | null = null;

	async reload() {
		this.loading = true;
		try {
			const result = await admin_fetchPlace(Number(this.props.params.placeId));
			if (result.result) {
				this.data = result.data.data;
				this.metadata = result.data.metadata;
			} else {
				toaster.show({
					type: 'error',
					message: result.error,
				});
			}
		} catch (err) {
			toaster.show({
				type: 'error',
				message: (err as Error).message,
			});
		}
		this.loading = false;
	}

	componentDidMount() {
		this.reload();
	}

	componentDidUpdate(prevProps: AdminPlaceCardProps) {
		if (prevProps.params.placeId !== this.props.params.placeId) {
			this.reload();
		}
	}

	handleClick(info: any) {
		console.log('info: ', info);
	}

	async handleAvatarUpload() {
		const newAvatarDataUri = await AvatarUploadModal.prompt();
		if (newAvatarDataUri) {
			this.avatarUploading = true;
			try {
				await manager_uploadPlaceAvatar(
					Number(this.props.params.placeId),
					await dataUriToBlob(newAvatarDataUri),
				);
				await this.reload();
			} catch (err) {
				handleError(err);
			}
			this.avatarUploading = false;
		}
	}

	async handleEditName() {
		const newName = await PromptModal.prompt('Change place name', this.data!.name);
		if (newName) {
			this.nameEditing = true;
			try {
				await manager_updatePlaceName(Number(this.props.params.placeId), newName);
				await this.reload();
			} catch (err) {
				handleError(err);
			}
			this.nameEditing = false;
		}
	}

	async handleEditPaymentDetails() {
		const newPaymentData = await PromptModal.prompt(
			'Change place payment details',
			this.data!.payment_data,
			undefined,
			undefined,
			'textarea',
		);
		if (newPaymentData) {
			this.paymentDataEditing = true;
			try {
				await manager_updatePlacePaymentData(Number(this.props.params.placeId), newPaymentData);
				await this.reload();
			} catch (err) {
				handleError(err);
			}
			this.paymentDataEditing = false;
		}
	}

	async handleConnect() {
		const newChainId = await SelectChainModal.show();
		if (!newChainId) {
			return;
		}
		this.placeDetaching = true;
		try {
			const response = await admin_attachPlace(Number(this.props.params.placeId), newChainId);
			if (response.result) {
				this.placeDetaching = false;
				toaster.show({ message: 'You successfully attached the place', type: 'success' });
				await this.reload();
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
	}

	async handleDisconnect() {
		const descision = await ConfirmModal.prompt(
			'Are you sure?',
			'It will detach this place from current chain. Chain admins will not be able to manage it anymore.',
			'Yes',
		);
		if (!descision) {
			return;
		}
		this.placeDetaching = true;
		try {
			const response = await admin_detachPlace(Number(this.props.params.placeId));
			if (response.result) {
				this.placeDetaching = false;
				toaster.show({ message: 'You successfully detached the place', type: 'success' });
				await this.reload();
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
	}

	async handleDelete() {
		const descision = await ConfirmModal.prompt(
			'Are you sure?',
			'This action is irreversible. You will lose all the data: employees, transactions, QR codes. Are you absolutely sure?',
			'Yes',
		);
		if (!descision) {
			return;
		}
		this.placeDeleting = true;
		try {
			const response = await admin_deletePlace(Number(this.props.params.placeId));
			if (response.result) {
				this.placeDeleting = false;
				toaster.show({ message: 'You successfully deleted the place', type: 'success' });
				this.props.navigate('/admin-places/');
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
	}

	async handleAttachEmployee() {
		const employeeId = await SelectEmployeeModal.show(null, Number(this.props.params.placeId), null, null, null);
		if (!employeeId) {
			return;
		}
		const data = await EmployeeRoleModal.prompt('Select employee role', UserPlaceRole.OWNER);
		if (!data) {
			return;
		}
		this.addEmployeeing = true;
		try {
			const response = await manager_attachEmployee(
				employeeId,
				Number(this.props.params.placeId),
				data.role,
				data.text_role,
			);
			if (response.result) {
				this.addEmployeeing = false;
				toaster.show({ message: 'You successfully attached the employee', type: 'success' });
				await this.reload();
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
	}

	async handleAddEmployee() {
		const result = await AddEmployeeModal.prompt(
			'Enter employee phone number',
			user.user!.places.find(p => p.id === Number(this.props.params.placeId))?.role || null,
			'+971',
			isValidPhone,
			'Please, enter UAE phone number of employee',
		);
		if (result) {
			const { role, phone } = result;
			this.employeeInviting = true;
			try {
				const response = await manager_invitePlaceEmployee(Number(this.props.params.placeId), phone, role);
				if (response.result) {
					const url = `${document.location.origin}/invite/${response.data}/`;
					await QRModal.prompt('Show this QR code to your employee', url);
				} else {
					handleError(response.error);
				}
			} catch (err) {
				handleError(err);
			}
			this.employeeInviting = false;
		}
	}

	async handleSimpleAddEmployee() {
		const phoneRole = await AddEmployeeModal.prompt(
			'Enter employee phone number',
			user.user!.places.find(p => p.id === Number(this.props.params.placeId))?.role || null,
			'+971',
			isValidPhone,
			'Please, enter UAE phone number of the employee',
		);
		if (phoneRole === null) {
			return;
		}
		const firstName = await PromptModal.prompt('Enter employee first name', '');
		if (firstName === null) {
			return;
		}
		const lastName = await PromptModal.prompt('Enter employee last name', '');
		if (lastName === null) {
			return;
		}
		this.employeeInviting = true;
		try {
			const response = await manager_introducePlaceEmployee(
				Number(this.props.params.placeId),
				phoneRole.phone,
				firstName,
				lastName,
				phoneRole.role,
			);
			if (response.result) {
				await this.reload();
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
		this.employeeInviting = false;
	}

	async handleExtraSimpleAddEmployee() {
		const result = await SimpleEmployeeModal.prompt(
			'Add employee',
			user.user!.places.find(p => p.id === Number(this.props.params.placeId))?.role || null,
		);
		if (!result) {
			return;
		}
		this.employeeInviting = true;
		try {
			const response = await manager_introducePlaceEmployee(
				Number(this.props.params.placeId),
				result.phone,
				result.firstName,
				result.lastName,
				result.role,
			);
			if (response.result) {
				await this.reload();
			} else {
				handleError(response.error);
			}
		} catch (err) {
			handleError(err);
		}
		this.employeeInviting = false;
	}

	render() {
		if (!this.data || !this.metadata) {
			return <Spin size="small" />;
		}

		return (
			<div className="page">
				<div className="page-head">
					<div className="page-title">
						<Typography.Title
							level={4}
							style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
						>
							<BackButton props={this.props} /> Place "{this.data.name}"
							{this.data.chain ? ` (Chain "${this.data.chain.name}")` : ''}
						</Typography.Title>
					</div>
					<div className="page-actions"></div>
				</div>
				<TPage className="section">
					<TPSidebar>
						<TPSidebarHeader>
							{this.avatarUploading ? (
								<Avatar size={120} src={null} icon={<Spin size="small" />} />
							) : (
								<Avatar size={120} src={this.data.logoUrl} icon={<HomeOutlined />} />
							)}
						</TPSidebarHeader>
						<Menu inlineIndent={12} selectable={false} onClick={this.handleClick} mode="inline">
							<Menu.Item
								key="1"
								icon={<UploadOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleAvatarUpload();
								}}
							>
								Upload new image
							</Menu.Item>
							<Menu.Item
								key="2"
								icon={this.nameEditing ? <Spin size="small" /> : <EditOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleEditName();
								}}
							>
								Edit name
							</Menu.Item>
							<Menu.Item
								key="5"
								icon={this.paymentDataEditing ? <Spin size="small" /> : <DollarCircleOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleEditPaymentDetails();
								}}
							>
								Edit payout details
							</Menu.Item>
							<Menu.Item
								key="3"
								icon={this.employeeInviting ? <Spin size="small" /> : <PlusOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleExtraSimpleAddEmployee();
								}}
							>
								Add new employee
							</Menu.Item>
							<Menu.Divider />
							<Menu.Item
								key="7"
								icon={this.addEmployeeing ? <Spin size="small" /> : <UserAddOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleAttachEmployee();
								}}
							>
								Attach employee
							</Menu.Item>
							{this.data.chainId ? (
								<Menu.Item
									key="6"
									danger
									icon={this.placeDetaching ? <Spin size="small" /> : <DisconnectOutlined />}
									onClick={e => {
										e.domEvent.preventDefault();
										this.handleDisconnect();
									}}
								>
									Detach from chain
								</Menu.Item>
							) : (
								<Menu.Item
									key="6"
									icon={this.placeDetaching ? <Spin size="small" /> : <AppstoreAddOutlined />}
									onClick={e => {
										e.domEvent.preventDefault();
										this.handleConnect();
									}}
								>
									Attach to chain
								</Menu.Item>
							)}
							<Menu.Divider />
							<Menu.Item
								key="4"
								danger
								icon={this.placeDeleting ? <Spin size="small" /> : <DeleteOutlined />}
								onClick={e => {
									e.domEvent.preventDefault();
									this.handleDelete();
								}}
							>
								Delete place
							</Menu.Item>
						</Menu>
					</TPSidebar>
					<TPContent>
						<StatHeader>
							<StatPlate>
								<StatPlateTitle>Employees</StatPlateTitle>
								<StatPlateValue>{formatNumber(this.metadata.employees_count)}</StatPlateValue>
							</StatPlate>
							<StatDiv />
							<StatPlate>
								<StatPlateTitle>7d tips volume</StatPlateTitle>
								<StatPlateValue>{formatMoney(this.metadata.tips_volume_7d)}</StatPlateValue>
							</StatPlate>
							<StatDiv />
							<StatPlate>
								<StatPlateTitle>Avg. tip volume</StatPlateTitle>
								<StatPlateValue>{formatMoney(this.metadata.average_tip_30d)}</StatPlateValue>
							</StatPlate>
						</StatHeader>
						<br />
						<div style={{ paddingLeft: 40 }}>
							{this.data.isTipzPro ? (
								<Button
									danger
									onClick={async () => {
										await manager_updatePlacePro(this.data!.id, false);
										await this.reload();
									}}
								>
									Revoke Tipz.Pro status
								</Button>
							) : (
								<Button
									onClick={async () => {
										await manager_updatePlacePro(this.data!.id, true);
										await this.reload();
									}}
								>
									Grant Tipz.Pro status
								</Button>
							)}
						</div>
					</TPContent>
				</TPage>
			</div>
		);
	}
}

export default withNavigateParams(AdminPlaceCard);
